/**
 * DialogueBox - Dialogue box with typewriter text effect
 */

import React, { useState, useEffect } from 'react';
import { playUIClick } from '../../game/audio/SoundEffects';

interface DialogueBoxProps {
  text: string;
  speaker?: string;
  portrait?: string; // URL to portrait image
  onComplete?: () => void;
  onSkip?: () => void;
  textSpeed?: number; // Characters per second
  autoAdvance?: boolean;
  autoAdvanceDelay?: number; // Milliseconds to wait after text completes
  visible?: boolean;
}

export const DialogueBox: React.FC<DialogueBoxProps> = ({
  text,
  speaker,
  portrait,
  onComplete,
  onSkip,
  textSpeed = 30,
  autoAdvance = false,
  autoAdvanceDelay = 2000,
  visible = true
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset state when text changes (new dialogue)
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
  }, [text]);

  // Typewriter effect
  useEffect(() => {
    if (!visible) {
      setDisplayedText('');
      setCurrentIndex(0);
      setIsComplete(false);
      return undefined;
    }

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.substring(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 1000 / textSpeed);

      return () => clearTimeout(timeout);
    } else if (!isComplete) {
      setIsComplete(true);
      onComplete?.();

      // Auto-advance if enabled
      if (autoAdvance) {
        const timeout = setTimeout(() => {
          handleSkip();
        }, autoAdvanceDelay);
        return () => clearTimeout(timeout);
      }
    }
    return undefined;
  }, [currentIndex, text, textSpeed, isComplete, visible, autoAdvance, autoAdvanceDelay, onComplete]);

  const handleSkip = () => {
    playUIClick(); // Play click sound for any skip action
    if (!isComplete) {
      // If text not complete, show all text instantly
      setDisplayedText(text);
      setCurrentIndex(text.length);
      setIsComplete(true);
      onComplete?.();
    } else {
      // If text complete, advance to next dialogue
      onSkip?.();
    }
  };

  if (!visible) return null;

  return (
    <div
      onClick={handleSkip}
      style={{
        position: 'fixed',
        bottom: '40px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80%',
        maxWidth: '800px',
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        border: '3px solid #00aaff',
        borderRadius: '12px',
        padding: '25px',
        fontFamily: 'monospace',
        color: '#ffffff',
        zIndex: 5000,
        cursor: 'pointer',
        boxShadow: '0 0 30px rgba(0, 170, 255, 0.3)',
      }}
    >
      <div style={{
        display: 'flex',
        gap: '20px',
      }}>
        {/* Portrait (if provided) */}
        {portrait && (
          <div style={{
            width: '100px',
            height: '100px',
            backgroundColor: '#222',
            border: '2px solid #00aaff',
            borderRadius: '8px',
            flexShrink: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src={portrait}
              alt="Portrait"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        {/* Text content */}
        <div style={{ flex: 1 }}>
          {/* Speaker name */}
          {speaker && (
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#00aaff',
              marginBottom: '10px',
              letterSpacing: '1px',
            }}>
              {speaker}
            </div>
          )}

          {/* Dialogue text with typewriter effect */}
          <div style={{
            fontSize: '18px',
            lineHeight: '1.6',
            color: '#ffffff',
            minHeight: '60px',
          }}>
            {displayedText}
            {!isComplete && (
              <span style={{
                display: 'inline-block',
                width: '8px',
                height: '20px',
                backgroundColor: '#00aaff',
                marginLeft: '4px',
                animation: 'blink 0.7s infinite',
              }} />
            )}
          </div>
        </div>
      </div>

      {/* Continue indicator */}
      {isComplete && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          right: '20px',
          fontSize: '12px',
          color: '#888',
          animation: 'pulse 1.5s infinite',
        }}>
          {autoAdvance ? 'Auto-advancing...' : 'Click to continue ▼'}
        </div>
      )}

      {/* Skip indicator */}
      {!isComplete && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '20px',
          fontSize: '10px',
          color: '#666',
        }}>
          Click to skip ⏭
        </div>
      )}

      {/* Animations */}
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }

          @keyframes pulse {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};
